import { useState, useEffect } from 'react';
import { Card, Typography, Empty, Spin, Tabs } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import { CountryFlag } from '../../components/Common/CountryFlag';
import {
  circuitsService,
  type Circuit,
  type CircuitWithGeoJSON,
} from '../../api/circuitsService';
import { climateRecapAnalyticsTabItems } from '../../components/ClimateRecap/climateRecapAnalyticsTabItems';
import { CircuitSelector } from '../../components/Common/CircuitSelector';
import { CircuitPreview } from '../../components/Map/CircuitPreview';
import { useTheme } from '../../contexts/useTheme';
import '../ClimateRecap/ClimateRecap.css';

const { Title, Text } = Typography;

const CIRCUITS_CLIMATE_STORAGE_KEY = 'fia-portal-circuits-climate-selected-circuit-id';

/** Legacy rows that share display name "TEST" — hide from circuit climate UI */
function isExcludedLegacyTestCircuit(c: Circuit): boolean {
  return c.display_name.trim().toUpperCase() === 'TEST';
}

function pickDefaultCircuitId(circuits: Circuit[]): string | null {
  if (circuits.length === 0) return null;
  const sorted = [...circuits].sort((a, b) =>
    a.display_name.localeCompare(b.display_name, undefined, { sensitivity: 'base' })
  );
  return sorted[0].circuit_id;
}

function readStoredCircuitId(): string | null {
  try {
    const raw = localStorage.getItem(CIRCUITS_CLIMATE_STORAGE_KEY);
    if (!raw) return null;
    const trimmed = raw.trim();
    return trimmed.length > 0 ? trimmed : null;
  } catch {
    return null;
  }
}

export const CircuitsClimatePage = () => {
  const { theme } = useTheme();
  const [circuits, setCircuits] = useState<Circuit[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [selectedCircuitId, setSelectedCircuitId] = useState<string | null>(null);
  const [circuitData, setCircuitData] = useState<CircuitWithGeoJSON | null>(null);
  const [loadingCircuit, setLoadingCircuit] = useState(false);

  useEffect(() => {
    if (loadingList || circuits.length === 0 || !selectedCircuitId) return;
    if (!circuits.some((c) => c.circuit_id === selectedCircuitId)) {
      setSelectedCircuitId(pickDefaultCircuitId(circuits));
    }
  }, [selectedCircuitId, circuits, loadingList]);

  useEffect(() => {
    if (loadingList) return;
    try {
      if (selectedCircuitId) {
        localStorage.setItem(CIRCUITS_CLIMATE_STORAGE_KEY, selectedCircuitId);
      } else {
        localStorage.removeItem(CIRCUITS_CLIMATE_STORAGE_KEY);
      }
    } catch (e) {
      console.error('Failed to persist circuits climate selection:', e);
    }
  }, [selectedCircuitId, loadingList]);

  useEffect(() => {
    let cancelled = false;
    setLoadingList(true);
    circuitsService
      .getAllCircuits(false)
      .then((list) => {
        if (cancelled) return;
        const nextCircuits = list.filter(
          (c) => !c.deleted && !isExcludedLegacyTestCircuit(c)
        );
        setCircuits(nextCircuits);

        const stored = readStoredCircuitId();
        const fromStorage =
          stored && nextCircuits.some((c) => c.circuit_id === stored)
            ? stored
            : null;
        setSelectedCircuitId(fromStorage ?? pickDefaultCircuitId(nextCircuits));
      })
      .catch((err: Error) => {
        console.error('Failed to load circuits:', err);
        if (!cancelled) {
          setCircuits([]);
          setSelectedCircuitId(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingList(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedCircuitId) {
      setCircuitData(null);
      return;
    }
    let cancelled = false;
    setLoadingCircuit(true);
    circuitsService
      .getCircuitById(selectedCircuitId)
      .then((data: CircuitWithGeoJSON) => {
        if (!cancelled) setCircuitData(data);
      })
      .catch((err: Error) => {
        console.error('Failed to load circuit data:', err);
        if (!cancelled) setCircuitData(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingCircuit(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedCircuitId]);

  const selectedCircuit = circuits.find((c) => c.circuit_id === selectedCircuitId) ?? null;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div className="climate-recap-page" style={{ flex: 1, overflowY: 'auto', padding: 0 }}>
        <Card className="event-header-card" variant="borderless">
          <div className="event-header-content">
            <div className="event-header-left">
              {selectedCircuit ? (
                <div className="event-flag">
                  <CountryFlag
                    country={selectedCircuit.country}
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '4px',
                      objectFit: 'cover',
                    }}
                    title={selectedCircuit.country}
                  />
                </div>
              ) : (
                <div className="event-flag event-flag--empty" aria-hidden />
              )}

              <div className="event-details">
                <div className="event-main-info">
                  <Title level={2} className="event-name">
                    {selectedCircuit?.display_name ?? 'Circuits'}
                  </Title>
                  <div className="circuits-header-circuit-select">
                    <CircuitSelector
                      circuits={circuits}
                      value={selectedCircuitId}
                      onChange={(id) => setSelectedCircuitId(id ?? null)}
                      loading={loadingList}
                      placeholder={loadingList ? 'Loading circuits…' : 'Select circuit'}
                    />
                  </div>
                </div>

                {selectedCircuit ? (
                  <div className="event-location">
                    <EnvironmentOutlined />
                    <Text strong>
                      {selectedCircuit.location_name || selectedCircuit.display_name}
                    </Text>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="event-circuit-preview">
              {!selectedCircuitId ? (
                <div className="circuit-preview-empty" />
              ) : loadingCircuit ? (
                <div className="circuit-loading">
                  <Spin size="small" />
                </div>
              ) : (
                <CircuitPreview
                  circuitId={selectedCircuitId}
                  theme={theme}
                  stations={[]}
                  circuitData={circuitData || undefined}
                />
              )}
            </div>
          </div>
        </Card>

        <Card className="climate-tabs-card" style={{ marginTop: 0 }}>
          {loadingList && !selectedCircuitId ? (
            <div style={{ padding: 48, textAlign: 'center' }}>
              <Spin />
            </div>
          ) : !selectedCircuitId ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Choose a circuit to view historical weather trends and compare sessions."
            />
          ) : (
            <Tabs
              defaultActiveKey="historical-weather"
              items={climateRecapAnalyticsTabItems(selectedCircuitId, {
                circuitTimeZone: selectedCircuit?.time_zone,
                stations: [],
                demo: false,
              })}
            />
          )}
        </Card>
      </div>
    </div>
  );
};
